import type { DateTime } from 'luxon'
import { useMemo } from 'react'

import type { FormattedRow } from '@/features/bessDashboard/types'
import type {
  HourlyPriceDataResult,
  HourlyQuantileBand,
  PriceLine,
  PricePoint,
} from '@/features/bidding/components/cascading/charts/types'
import type { BidLevelInput } from '@/features/bidding/types/bidLadderInput'
import type { BidLevelInputSymmetrical } from '@/features/bidding/types/bidLadderInputSymmetrical'
import type { ForecastMarketPair, PricingStrategyConfig } from '@/features/bidding/types/forecastPricing'
import {
  deriveForecastPricesForDirection,
  getPriceForHour,
} from '@/features/bidding/utils/cascading/bidLadder/deriveForecastPrices'
import { getMarketTimezone } from '@/features/bidding/utils/forecast/convertForecastTimezone'
import { extractHourlyQuantilePrices } from '@/features/bidding/utils/forecast/extractHourlyQuantilePrices'
import {
  getMarketProgramNameForDirection,
  isSymmetricMarket,
} from '@/features/bidding/utils/forecast/marketProgramNames'

const HOURS_PER_DAY = 24
const UP_COLOR = '#16a34a' // green-600
const DOWN_COLOR = '#dc2626' // red-600
const SYMMETRICAL_COLOR = '#1976d2' // MUI primary blue

type UseHourlyPriceDataParams = {
  levels: BidLevelInput[] | BidLevelInputSymmetrical[]
  market: ForecastMarketPair
  baseDate: Date
  pricingConfig?: PricingStrategyConfig
  forecastsData?: FormattedRow[]
  selectedDeliveryDay?: DateTime
}

/**
 * Helper to check if levels are symmetrical (no direction field on groups)
 */
function isSymmetricalLevels(
  levels: BidLevelInput[] | BidLevelInputSymmetrical[],
): levels is BidLevelInputSymmetrical[] {
  if (levels.length === 0) return false
  // Check if levels have 'price' field (symmetrical) vs 'upPrice'/'downPrice' (directional)
  return 'price' in levels[0]
}

/**
 * Transforms bid level inputs into price line chart data.
 *
 * For directional markets:
 * - UP direction prices are positive
 * - DOWN direction prices are negative
 * - Extracts quantile bands for both directions
 *
 * For symmetrical markets:
 * - All prices are positive (single line per level)
 * - Only UP quantile bands (used for both directions visually)
 *
 * Supports both directional (BidLevelInput) and symmetrical (BidLevelInputSymmetrical) inputs.
 */
export function useHourlyPriceData({
  levels,
  market,
  baseDate,
  pricingConfig,
  forecastsData,
  selectedDeliveryDay,
}: UseHourlyPriceDataParams): HourlyPriceDataResult {
  return useMemo(() => {
    const isSymmetrical = isSymmetricalLevels(levels) || isSymmetricMarket(market)
    const datetimes = createHourlyDatetimes(baseDate)

    if (isSymmetrical) {
      const symmetricalLevels = levels as BidLevelInputSymmetrical[]
      const maxPrice = calculateMaxPriceSymmetrical(
        symmetricalLevels,
        pricingConfig,
        forecastsData,
        selectedDeliveryDay,
      )
      const lines = createPriceLinesSymmetrical(symmetricalLevels, pricingConfig, forecastsData, selectedDeliveryDay)
      const upQuantileBands = extractQuantileBands(forecastsData, market, 'up', selectedDeliveryDay)

      return {
        lines,
        datetimes,
        maxPrice,
        hasData: maxPrice > 0,
        upQuantileBands,
        downQuantileBands: null, // Symmetrical markets don't need separate DOWN bands
      }
    }

    const directionalLevels = levels as BidLevelInput[]
    const maxPrice = calculateMaxPrice(directionalLevels, pricingConfig, forecastsData, selectedDeliveryDay)
    const lines = createPriceLines(directionalLevels, pricingConfig, forecastsData, selectedDeliveryDay)
    const upQuantileBands = extractQuantileBands(forecastsData, market, 'up', selectedDeliveryDay)
    const downQuantileBands = extractQuantileBands(forecastsData, market, 'down', selectedDeliveryDay)

    return {
      lines,
      datetimes,
      maxPrice,
      hasData: maxPrice > 0,
      upQuantileBands,
      downQuantileBands,
    }
  }, [levels, market, baseDate, pricingConfig, forecastsData, selectedDeliveryDay])
}

/**
 * Creates hourly datetime points for the x-axis
 */
function createHourlyDatetimes(baseDate: Date): Date[] {
  const datetimes: Date[] = []
  for (let hour = 0; hour < HOURS_PER_DAY; hour++) {
    const datetime = new Date(baseDate)
    datetime.setHours(hour, 0, 0, 0)
    datetimes.push(datetime)
  }
  return datetimes
}

/**
 * Calculates the maximum price across all bid levels to determine chart scale.
 * This is calculated from all levels regardless of assignment.
 */
function calculateMaxPrice(
  levels: BidLevelInput[],
  pricingConfig: PricingStrategyConfig | undefined,
  forecastsData: FormattedRow[] | undefined,
  selectedDeliveryDay: DateTime | undefined,
): number {
  let maxPrice = 0

  for (let levelIndex = 0; levelIndex < levels.length; levelIndex++) {
    const level = levels[levelIndex]
    const levelConfig = pricingConfig?.levels[levelIndex]
    const isForecastMode = levelConfig?.mode === 'forecast'

    // Get prices for both directions (ignoring assignment)
    const upPrices = getHourlyPricesForLevel(
      level,
      'up',
      isForecastMode,
      levelConfig,
      forecastsData,
      selectedDeliveryDay,
      false,
    )
    const downPrices = getHourlyPricesForLevel(
      level,
      'down',
      isForecastMode,
      levelConfig,
      forecastsData,
      selectedDeliveryDay,
      false,
    )

    // Track maximum price
    for (const point of [...upPrices, ...downPrices]) {
      if (point.price > maxPrice) {
        maxPrice = point.price
      }
    }
  }

  return maxPrice
}

/**
 * Creates price lines for levels with assigned activation groups.
 * Only levels with assignments will have visible lines on the chart.
 */
function createPriceLines(
  levels: BidLevelInput[],
  pricingConfig: PricingStrategyConfig | undefined,
  forecastsData: FormattedRow[] | undefined,
  selectedDeliveryDay: DateTime | undefined,
): PriceLine[] {
  const lines: PriceLine[] = []

  for (let levelIndex = 0; levelIndex < levels.length; levelIndex++) {
    const level = levels[levelIndex]
    const levelConfig = pricingConfig?.levels[levelIndex]
    const isForecastMode = levelConfig?.mode === 'forecast'

    // Add UP direction line if assigned
    const upLine = createPriceLineForDirection(
      level,
      levelIndex,
      'up',
      UP_COLOR,
      isForecastMode,
      levelConfig,
      forecastsData,
      selectedDeliveryDay,
    )
    if (upLine) {
      lines.push(upLine)
    }

    // Add DOWN direction line if assigned (with negated prices)
    const downLine = createPriceLineForDirection(
      level,
      levelIndex,
      'down',
      DOWN_COLOR,
      isForecastMode,
      levelConfig,
      forecastsData,
      selectedDeliveryDay,
    )
    if (downLine) {
      // Negate prices for DOWN direction to display below zero line
      const negatedPoints = downLine.points.map((p) => ({ ...p, price: -p.price }))
      lines.push({ ...downLine, points: negatedPoints })
    }
  }

  return lines
}

/**
 * Creates a single price line for a bid level in a specific direction.
 * Returns null if the level has no assigned groups in this direction.
 */
function createPriceLineForDirection(
  level: BidLevelInput,
  levelIndex: number,
  direction: 'up' | 'down',
  color: string,
  isForecastMode: boolean,
  levelConfig: PricingStrategyConfig['levels'][0] | undefined,
  forecastsData: FormattedRow[] | undefined,
  selectedDeliveryDay: DateTime | undefined,
): PriceLine | null {
  const points = getHourlyPricesForLevel(
    level,
    direction,
    isForecastMode,
    levelConfig,
    forecastsData,
    selectedDeliveryDay,
    true, // Only get points if groups are assigned
  )

  if (points.length === 0) {
    return null
  }

  return {
    levelId: level.id,
    levelIndex,
    direction,
    points,
    color,
  }
}

/**
 * Gets hourly price points for a bid level in a specific direction.
 * Can optionally filter by assignment status.
 */
function getHourlyPricesForLevel(
  level: BidLevelInput,
  direction: 'up' | 'down',
  isForecastMode: boolean,
  levelConfig: PricingStrategyConfig['levels'][0] | undefined,
  forecastsData: FormattedRow[] | undefined,
  selectedDeliveryDay: DateTime | undefined,
  checkAssignment: boolean,
): PricePoint[] {
  // Early return if checking assignment and no groups assigned
  if (checkAssignment && !hasAssignedGroupsForDirection(level, direction)) {
    return []
  }

  // Get prices based on pricing mode
  if (isForecastMode && levelConfig && forecastsData && selectedDeliveryDay) {
    return getForecastPrices(forecastsData, levelConfig, direction, selectedDeliveryDay)
  }

  const fixedPrice = direction === 'up' ? level.upPrice : level.downPrice
  if (fixedPrice !== null) {
    return getFixedPrices(fixedPrice)
  }

  return []
}

/**
 * Checks if a level has assigned groups or volume capacity in a specific direction
 */
function hasAssignedGroupsForDirection(level: BidLevelInput, direction: 'up' | 'down'): boolean {
  if (level.assignedGroups.some((g) => g.direction === direction)) return true
  const config = direction === 'up' ? level.upVolumeCapacity : level.downVolumeCapacity
  return config?.value != null && config.value > 0
}

/**
 * Gets forecast-based prices for all hours
 */
function getForecastPrices(
  forecastsData: FormattedRow[],
  levelConfig: PricingStrategyConfig['levels'][0],
  direction: 'up' | 'down',
  selectedDeliveryDay: DateTime,
): PricePoint[] {
  const points: PricePoint[] = []
  const hourlyPrices = deriveForecastPricesForDirection(
    forecastsData,
    levelConfig.market,
    direction,
    levelConfig.quantile,
    selectedDeliveryDay,
  )

  for (let hour = 0; hour < HOURS_PER_DAY; hour++) {
    const price = getPriceForHour(hourlyPrices, hour)
    if (price !== null) {
      points.push({ hour, price })
    }
  }

  return points
}

/**
 * Gets fixed prices for all hours (same value across the day)
 */
function getFixedPrices(fixedPrice: number): PricePoint[] {
  const points: PricePoint[] = []
  for (let hour = 0; hour < HOURS_PER_DAY; hour++) {
    points.push({ hour, price: fixedPrice })
  }
  return points
}

/**
 * Extracts quantile bands (P10-P90) from forecast data for background visualization.
 * Returns null if no forecast data available or market doesn't support quantiles.
 */
function extractQuantileBands(
  forecastsData: FormattedRow[] | undefined,
  market: ForecastMarketPair,
  direction: 'up' | 'down',
  selectedDeliveryDay: DateTime | undefined,
): HourlyQuantileBand[] | null {
  if (!canExtractQuantileBands(forecastsData, market, selectedDeliveryDay)) {
    return null
  }

  const marketProgramName = getMarketProgramNameForDirection(market, direction)
  const marketTimezone = getMarketTimezone()
  const dailyPrices = extractHourlyQuantilePrices(
    forecastsData!,
    marketProgramName,
    selectedDeliveryDay!,
    marketTimezone,
  )

  if (!dailyPrices) {
    return null
  }

  return buildQuantileBands(dailyPrices.hourlyPrices)
}

/**
 * Checks if quantile bands can be extracted
 */
function canExtractQuantileBands(
  forecastsData: FormattedRow[] | undefined,
  market: ForecastMarketPair,
  selectedDeliveryDay: DateTime | undefined,
): boolean {
  return Boolean(forecastsData && selectedDeliveryDay && market !== 'opportunity-cost')
}

/**
 * Builds quantile band array from hourly price data
 */
function buildQuantileBands(
  hourlyPricesMap: Map<number, { p10: number; p25: number; p75: number; p90: number }>,
): HourlyQuantileBand[] | null {
  const bands: HourlyQuantileBand[] = []

  for (let hour = 0; hour < HOURS_PER_DAY; hour++) {
    const hourlyPrices = hourlyPricesMap.get(hour)
    if (hourlyPrices) {
      bands.push({
        hour,
        p10: hourlyPrices.p10,
        p25: hourlyPrices.p25,
        p75: hourlyPrices.p75,
        p90: hourlyPrices.p90,
      })
    }
  }

  return bands.length > 0 ? bands : null
}

// ============================================
// Symmetrical market helpers
// ============================================

/**
 * Calculates the maximum price for symmetrical markets.
 */
function calculateMaxPriceSymmetrical(
  levels: BidLevelInputSymmetrical[],
  pricingConfig: PricingStrategyConfig | undefined,
  forecastsData: FormattedRow[] | undefined,
  selectedDeliveryDay: DateTime | undefined,
): number {
  let maxPrice = 0

  for (let levelIndex = 0; levelIndex < levels.length; levelIndex++) {
    const level = levels[levelIndex]
    const levelConfig = pricingConfig?.levels[levelIndex]
    const isForecastMode = levelConfig?.mode === 'forecast'

    const prices = getHourlyPricesForLevelSymmetrical(
      level,
      isForecastMode,
      levelConfig,
      forecastsData,
      selectedDeliveryDay,
      false,
    )

    for (const point of prices) {
      if (point.price > maxPrice) {
        maxPrice = point.price
      }
    }
  }

  return maxPrice
}

/**
 * Creates price lines for symmetrical markets.
 */
function createPriceLinesSymmetrical(
  levels: BidLevelInputSymmetrical[],
  pricingConfig: PricingStrategyConfig | undefined,
  forecastsData: FormattedRow[] | undefined,
  selectedDeliveryDay: DateTime | undefined,
): PriceLine[] {
  const lines: PriceLine[] = []

  for (let levelIndex = 0; levelIndex < levels.length; levelIndex++) {
    const level = levels[levelIndex]
    const levelConfig = pricingConfig?.levels[levelIndex]
    const isForecastMode = levelConfig?.mode === 'forecast'

    const points = getHourlyPricesForLevelSymmetrical(
      level,
      isForecastMode,
      levelConfig,
      forecastsData,
      selectedDeliveryDay,
      true,
    )

    if (points.length > 0) {
      lines.push({
        levelId: level.id,
        levelIndex,
        direction: 'up', // Symmetrical markets use 'up' as the single direction
        points,
        color: SYMMETRICAL_COLOR,
      })
    }
  }

  return lines
}

/**
 * Gets hourly price points for a symmetrical bid level.
 */
function getHourlyPricesForLevelSymmetrical(
  level: BidLevelInputSymmetrical,
  isForecastMode: boolean,
  levelConfig: PricingStrategyConfig['levels'][0] | undefined,
  forecastsData: FormattedRow[] | undefined,
  selectedDeliveryDay: DateTime | undefined,
  checkAssignment: boolean,
): PricePoint[] {
  // Early return if checking assignment and no groups or volume capacity assigned
  if (checkAssignment && level.assignedGroups.length === 0) {
    const hasVolumeCapacity = level.volumeCapacity?.value != null && level.volumeCapacity.value > 0
    if (!hasVolumeCapacity) return []
  }

  // Get prices based on pricing mode
  if (isForecastMode && levelConfig && forecastsData && selectedDeliveryDay) {
    return getForecastPrices(forecastsData, levelConfig, 'up', selectedDeliveryDay)
  }

  if (level.price !== null) {
    return getFixedPrices(level.price)
  }

  return []
}
